/* Laceup → EcomVault Pay. Prices and shipping are confirmed on the server. */
(() => {
  const script = document.querySelector(
    "script[data-laceup-ecomvault-checkout]",
  );
  if (!script || window.laceupEcomVaultLoaded) return;
  const origin = script.dataset.checkoutOrigin;
  if (
    origin !==
    "https://ecomvault-pay-trial-20261029.still-wind-6f53.workers.dev"
  )
    return;
  window.laceupEcomVaultLoaded = true;
  const root = window.Shopify?.routes?.root || "/";
  const fallback = new WeakSet();
  let busy = false;
  let mutations = 0;
  let lastMutation = 0;
  // The one threshold gift is recreated and validated by the checkout server.
  const giftVariantIds = new Set([
    "59247740485896",
    "59247740518664",
    "59247740551432",
    "59247740584200",
    "59247740616968",
  ]);
  const thresholdGift = (item) =>
    giftVariantIds.has(String(item.variant_id)) &&
    item.properties?._laceup_gift === "free-100" &&
    Object.keys(item.properties).every((key) => key === "_laceup_gift");
  const originalFetch = window.fetch.bind(window);
  // Preserve the incumbent set guard and observe only cart mutations.
  window.fetch = function (input, init) {
    const url = new URL(
      typeof input === "string" ? input : input.url,
      location.href,
    );
    const cartMutation =
      url.origin === location.origin &&
      /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
    if (!cartMutation) return originalFetch(input, init);
    mutations++;
    lastMutation = Date.now();
    return originalFetch(input, init).finally(() => {
      mutations--;
      lastMutation = Date.now();
    });
  };
  const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  async function settle() {
    const started = Date.now();
    // Dawn debounces quantity changes; allow that update to start before reading.
    do {
      await pause(100);
      if (Date.now() - started > 10_000)
        throw Error(
          "Je winkelwagen wordt nog bijgewerkt. Wacht even en probeer opnieuw.",
        );
    } while (mutations || Date.now() - Math.max(started, lastMutation) < 650);
  }
  async function json(url, options) {
    const response = await window.fetch(url, {
      ...options,
      signal: AbortSignal.timeout(20_000),
    });
    const value = await response.json().catch(() => null);
    if (!response.ok || !value)
      throw Error(
        value?.error || "De checkout is niet bereikbaar. Probeer opnieuw.",
      );
    return value;
  }
  async function checkout(form, button) {
    if (busy) return;
    busy = true;
    const previous = button.textContent;
    button.disabled = true;
    button.textContent = "Checkout openen…";
    const errors = document.querySelectorAll("[data-ecomvault-checkout-error]");
    errors.forEach((node) => node.remove());
    try {
      await settle();
      const note =
        form.querySelector('[name="note"]') ||
        document.querySelector('cart-note [name="note"]');
      if (note)
        await json(`${root}cart/update.js`, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ note: note.value }),
        });
      const cart = await json(`${root}cart.js`, {
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      if (!cart.items?.length)
        throw Error("Je winkelwagen is leeg. Voeg eerst een artikel toe.");
      // Preserve native checkout for sets, gifts, subscriptions and personalization.
      // Never discard their properties or submit a free set component on its own.
      if (
        cart.currency !== "EUR" ||
        Object.keys(cart.attributes || {}).length ||
        cart.items.some(
          (item) =>
            item.selling_plan_allocation ||
            item.gift_card ||
            item.requires_components ||
            item.price <= 0 ||
            (giftVariantIds.has(String(item.variant_id)) &&
              !thresholdGift(item)) ||
            (Object.keys(item.properties || {}).length && !thresholdGift(item)),
        )
      ) {
        fallback.add(form);
        button.disabled = false;
        button.textContent = previous;
        form.requestSubmit(button);
        return;
      }
      const lines = new Map();
      const selectedGift = cart.items.find(thresholdGift);
      for (const item of cart.items) {
        if (thresholdGift(item)) continue;
        const id = String(item.variant_id);
        lines.set(id, (lines.get(id) || 0) + item.quantity);
      }
      const result = await json(`${origin}/api/v1/storefront/laceup/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cartToken: cart.token,
          ...(selectedGift
            ? { giftVariantId: String(selectedGift.variant_id) }
            : {}),
          items: [...lines].map(([variantId, quantity]) => ({
            variantId,
            quantity,
          })),
          discountCodes: (cart.discount_codes || [])
            .filter((code) => code.applicable !== false)
            .map((code) => code.code),
          note: cart.note || "",
        }),
      });
      const destination = new URL(result.url);
      if (
        ![origin, "https://checkout.laceupfashion.nl"].includes(
          destination.origin,
        ) ||
        destination.username ||
        destination.password ||
        destination.pathname !== "/storefront-checkout" ||
        !/^[a-f0-9]{64}$/.test(destination.searchParams.get("session") || "")
      )
        throw Error(
          "De checkoutlink kon niet worden gecontroleerd. Probeer opnieuw.",
        );
      location.assign(destination.href);
    } catch (error) {
      const message = document.createElement("p");
      message.dataset.ecomvaultCheckoutError = "";
      message.setAttribute("role", "alert");
      message.textContent =
        error instanceof Error ? error.message : "Open de checkout opnieuw.";
      button.insertAdjacentElement("afterend", message);
      button.disabled = false;
      button.textContent = previous;
    } finally {
      busy = false;
    }
  }
  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      const button = event.submitter;
      if (!(form instanceof HTMLFormElement) || button?.name !== "checkout")
        return;
      if (fallback.has(form)) {
        fallback.delete(form);
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      void checkout(form, button);
    },
    true,
  );
  window.addEventListener("pageshow", () => {
    busy = false;
    document.querySelectorAll('[name="checkout"]').forEach((button) => {
      if (button.textContent === "Checkout openen…") {
        button.disabled = false;
        button.textContent = "Afrekenen";
      }
    });
  });
})();
